import { Helmet } from "react-helmet";
import { useState } from "react";
import {
  FaDatabase,
  FaDollarSign,
  FaGaugeHigh,
  FaTag,
  FaArrowTrendUp,
  FaUser,
  FaPenToSquare,
  FaMagnifyingGlass,
  FaPlus,
  FaTrash,
  FaXmark,
  FaSquare,
} from "react-icons/fa6";
import { PppoeProfileCreate } from "./PppoeCreate";
import { BtnDefault } from "../../../../components/buttons/BtnDefault";
import { Header } from "../../../../components/Header";
import { useHookPppoeProfile } from "../../../../hooks/useHookPppoeProfile";
import { DashboardLayout } from "../../../../components/layouts/DashboardLayout";
import { PppoeProfileService } from "../../../../services/PppoeProfileService";
import { toast } from "react-toastify";
import { FaCheckSquare } from "react-icons/fa";

/* ============================================
   Skeleton Loading
   ============================================ */
const SkeletonRow = ({ cols = 7 }) => (
  <tr className="animate-pulse">
    {Array.from({ length: cols }).map((_, i) => (
      <td key={i} className="px-5 py-4">
        <div
          className="h-3.5 bg-slate-700/50 rounded-md"
          style={{ width: `${35 + Math.random() * 55}%` }}
        />
      </td>
    ))}
  </tr>
);

const SkeletonCard = () => (
  <div
    className="rounded-2xl p-5 animate-pulse"
    style={{
      background: "rgba(15, 23, 42, 0.5)",
      border: "1px solid rgba(51, 65, 85, 0.3)",
    }}
  >
    <div className="flex justify-between items-start mb-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-slate-700/50" />
        <div className="space-y-2">
          <div className="h-4 bg-slate-700/50 rounded-md w-28" />
          <div className="h-3 bg-slate-700/30 rounded-md w-16" />
        </div>
      </div>
      <div className="h-6 bg-slate-700/30 rounded-full w-16" />
    </div>
    <div className="space-y-3 pt-3" style={{ borderTop: "1px solid rgba(51, 65, 85, 0.15)" }}>
      <div className="h-12 bg-slate-700/30 rounded-xl" />
      <div className="grid grid-cols-2 gap-3">
        <div className="h-14 bg-slate-700/25 rounded-xl" />
        <div className="h-14 bg-slate-700/25 rounded-xl" />
      </div>
    </div>
  </div>
);

/* ============================================
   Status Badge
   ============================================ */
const StatusBadge = ({ status }) => {
  const isActive = status === "aktif";
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold tracking-wide ${
        isActive
          ? "bg-emerald-500/12 text-emerald-400 border border-emerald-500/20"
          : "bg-red-500/12 text-red-400 border border-red-500/20"
      }`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${isActive ? "bg-emerald-400" : "bg-red-400"}`} />
      {status}
    </span>
  );
};

/* ============================================
   Format Rupiah
   ============================================ */
const formatRupiah = (value) => {
  if (!value && value !== 0) return "-";
  return `Rp ${Number(value).toLocaleString("id-ID")}`;
};

/* ============================================
   Confirm Modal (Inline)
   ============================================ */
const ConfirmModal = ({ isOpen, onClose, onSubmit, loading, title, children, submitText, danger }) => {
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!loading && onSubmit) {
      onSubmit(e);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={!loading ? onClose : undefined} />
      <form onSubmit={handleSubmit}>
        <div
          className="relative w-full max-w-md rounded-2xl p-6 shadow-2xl"
          style={{
            background: "rgba(15, 23, 42, 0.95)",
            border: "1px solid rgba(51, 65, 85, 0.5)",
            backdropFilter: "blur(20px)",
          }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="absolute top-4 right-4 p-1.5 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-700/50 transition-colors disabled:opacity-50"
          >
            <FaXmark className="text-sm" />
          </button>

          {title && <h3 className="text-lg font-display font-bold text-gray-100 mb-4 pr-8">{title}</h3>}

          <div className="mb-6">{children}</div>

          <div className="grid grid-cols-2 gap-4">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-5 py-2.5 rounded-xl text-sm font-semibold text-slate-300 bg-slate-700/50 border border-slate-600/30 hover:bg-slate-700 transition-all disabled:opacity-50"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-all disabled:opacity-50 flex items-center justify-center gap-2 text-cener ${
                danger
                  ? "text-white bg-red-500/80 hover:bg-red-500 border border-red-500/30"
                  : "text-white bg-cyan-600 hover:bg-cyan-500 border border-cyan-500/30"
              }`}
            >
              {loading && (
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              )}
              {submitText || "Konfirmasi"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

/* ============================================
   KOMPONEN: PppoeProfile
   ============================================ */
export const PppoeProfile = () => {
  const [showSidebar, setShowSidebar] = useState(false);
  const [openModalCreate, setOpenModalCreate] = useState(false);

  // Delete states
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedDelete, setSelectedDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Bulk delete states
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleteModal, setBulkDeleteModal] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const { data, loading, refreshData, keyword, setKeyword } = useHookPppoeProfile();

  /* ---------- Checkbox Handlers ---------- */
  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === data?.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(data?.map((item) => item.id) || []);
    }
  };

  const isSelected = (id) => selectedIds.includes(id);
  const isAllSelected = data?.length > 0 && selectedIds.length === data?.length;

  /* ---------- Delete Single Handler ---------- */
  const handleDeleteClick = (item) => {
    setSelectedDelete(item);
    setDeleteModal(true);
  };

  const handleDeleteSubmit = async (e) => {
    e.preventDefault();
    setDeleting(true);
    try {
      await PppoeProfileService.delete({ id: selectedDelete.id });
      toast.success(`Profile "${selectedDelete.name}" berhasil dihapus`);
      setDeleteModal(false);
      setSelectedDelete(null);
      setSelectedIds((prev) => prev.filter((id) => id !== selectedDelete.id));
      refreshData();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Gagal menghapus profile");
    } finally {
      setDeleting(false);
    }
  };

  /* ---------- Bulk Delete Handler ---------- */
  const handleBulkDeleteClick = () => {
    if (selectedIds.length === 0) return;
    setBulkDeleteModal(true);
  };

  const handleBulkDeleteSubmit = async (e) => {
    e.preventDefault();
    setBulkDeleting(true);
    try {
      const response = await PppoeProfileService.bulkDelete({ ids: selectedIds });
      const deleted = response?.data?.deleted_count || selectedIds.length;
      const skipped = response?.data?.skipped_count || 0;

      if (skipped > 0) {
        toast.warning(`${deleted} profile dihapus, ${skipped} dilewati karena sedang digunakan`);
      } else {
        toast.success(`${deleted} profile berhasil dihapus`);
      }

      setBulkDeleteModal(false);
      setSelectedIds([]);
      refreshData();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Gagal menghapus profile");
    } finally {
      setBulkDeleting(false);
    }
  };

  /* ---------- Table Columns ---------- */
  const columns = [
    { key: "checkbox", label: "", align: "center", width: "50px" },
    { key: "no", label: "No", align: "center", width: "60px" },
    { key: "name", label: "Nama Profile", align: "left" },
    { key: "price", label: "Harga Jual", align: "left" },
    { key: "reseller_fee", label: "Reseller Fee", align: "left" },
    { key: "rate_limit", label: "Rate Limit", align: "left" },
    { key: "action", label: "Aksi", align: "center", width: "80px" },
  ];

  return (
    <>
      <Helmet>
        <title>PPPoE Profile - Radius Trend</title>
        <meta
          name="description"
          content="Kelola data PPPoE profile — atur harga jual, reseller fee, rate limit, dan status profile pelanggan ISP."
        />
      </Helmet>

      <DashboardLayout showSidebar={showSidebar} setShowSidebar={setShowSidebar}>
        {/* ===== Header ===== */}
        <Header toggleSidebar={() => setShowSidebar(!showSidebar)}>
          <h2 className="text-base md:text-xl font-display font-bold text-gray-100">
            Management PPPoE Profile
          </h2>
          <div className="flex items-center gap-2">
            {selectedIds.length > 0 && (
              <button
                onClick={handleBulkDeleteClick}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-red-400 bg-red-500/10 border border-red-500/20 hover:bg-red-500/20 transition-all duration-200"
              >
                <FaTrash className="text-xs" />
                <span className="hidden sm:inline">Hapus ({selectedIds.length})</span>
                <span className="sm:hidden">{selectedIds.length}</span>
              </button>
            )}
            <BtnDefault onClick={() => setOpenModalCreate(true)}>
              <FaPlus className="text-xs hidden sm:block" />
              <span>Tambah Profile</span>
            </BtnDefault>
          </div>
        </Header>

        {/* ===== Content ===== */}
        <main className="p-4 lg:p-6 space-y-5">
          {/* ===== Search ===== */}
          <section
            className="rounded-2xl p-4"
            style={{
              background: "rgba(15, 23, 42, 0.5)",
              border: "1px solid rgba(51, 65, 85, 0.3)",
              backdropFilter: "blur(12px)",
            }}
            aria-label="Pencarian"
          >
            <div className="w-full max-w-sm">
              <div className="relative">
                <FaMagnifyingGlass className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-sm" />
                <input
                  type="text"
                  placeholder="Cari nama profile..."
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 placeholder-slate-500 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all"
                />
              </div>
            </div>
          </section>

          {/* ===== Desktop Table ===== */}
          <section
            className="hidden lg:block rounded-2xl overflow-hidden"
            style={{
              background: "rgba(15, 23, 42, 0.5)",
              border: "1px solid rgba(51, 65, 85, 0.3)",
              backdropFilter: "blur(12px)",
            }}
            aria-label="Tabel PPPoE profile"
          >
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b" style={{ background: "rgba(15, 23, 42, 0.7)", borderColor: "rgba(51, 65, 85, 0.3)" }}>
                    {columns.map((col) => (
                      <th
                        key={col.key}
                        className={`px-5 py-3.5 text-xs font-semibold uppercase tracking-wider text-slate-400 ${
                          col.align === "center" ? "text-center" : "text-left"
                        }`}
                        style={col.width ? { width: col.width } : undefined}
                      >
                        {col.key === "checkbox" ? (
                          <button onClick={toggleSelectAll} className="text-slate-400 hover:text-cyan-400 transition-colors">
                            {isAllSelected ? <FaCheckSquare className="text-cyan-400 text-sm" /> : <FaSquare className="text-sm" />}
                          </button>
                        ) : (
                          col.label
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody className="divide-y" style={{ borderColor: "rgba(51, 65, 85, 0.15)" }}>
                  {loading && !data && <>{[...Array(5)].map((_, i) => <SkeletonRow key={i} cols={columns.length} />)}</>}

                  {!loading && data?.length === 0 && (
                    <tr>
                      <td colSpan={columns.length} className="px-5 py-16 text-center">
                        <div className="flex flex-col items-center gap-3">
                          <div
                            className="w-16 h-16 rounded-2xl flex items-center justify-center"
                            style={{ background: "rgba(51, 65, 85, 0.2)", border: "1px solid rgba(51, 65, 85, 0.2)" }}
                          >
                            <FaDatabase className="text-2xl text-slate-600" />
                          </div>
                          <div>
                            <p className="text-slate-400 font-medium">Tidak ada profile ditemukan</p>
                            <p className="text-xs text-slate-500 mt-1">
                              {keyword ? "Coba ubah kata kunci pencarian" : "Belum ada PPPoE profile yang terdaftar"}
                            </p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}

                  {!loading &&
                    data?.map((item, index) => (
                      <tr
                        key={item.id}
                        className="transition-colors duration-150"
                        style={{ background: isSelected(item.id) ? "rgba(0, 180, 216, 0.05)" : "transparent" }}
                        onMouseEnter={(e) => {
                          if (!isSelected(item.id)) e.currentTarget.style.background = "rgba(0, 180, 216, 0.04)";
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected(item.id)) e.currentTarget.style.background = "transparent";
                        }}
                      >
                        <td className="px-5 py-4 text-center">
                          <button onClick={() => toggleSelect(item.id)} className="text-slate-400 hover:text-cyan-400 transition-colors">
                            {isSelected(item.id) ? <FaCheckSquare className="text-cyan-400 text-sm" /> : <FaSquare className="text-sm" />}
                          </button>
                        </td>

                        <td className="px-5 py-4 text-center text-slate-500 font-mono text-xs">{index + 1}</td>

                        <td className="px-5 py-4">
                          <span className="font-semibold text-gray-100">{item.name}</span>
                        </td>

                        <td className="px-5 py-4">
                          <span className="font-mono text-sm text-gray-200 bg-cyan-500/8 px-2.5 py-1 rounded-lg border border-cyan-500/10">
                            {formatRupiah(item.price)}
                          </span>
                        </td>

                        <td className="px-5 py-4">
                          <span className="font-mono text-sm text-emerald-400 bg-emerald-500/8 px-2.5 py-1 rounded-lg border border-emerald-500/10">
                            {formatRupiah(item.reseller_fee)}
                          </span>
                        </td>

                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2">
                            <FaGaugeHigh className="text-xs text-slate-500" />
                            <span className="text-slate-300 text-sm">{item.rate_limit || "Unlimited"}</span>
                          </div>
                        </td>

                        <td className="px-5 py-4 text-center">
                          <button
                            onClick={() => handleDeleteClick(item)}
                            className="p-2 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-all duration-200"
                            title="Hapus"
                          >
                            <FaTrash className="text-xs" />
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* ===== Mobile Card List ===== */}
          <section className="lg:hidden space-y-3" aria-label="Daftar PPPoE profile">
            {loading && !data && <>{[...Array(4)].map((_, i) => <SkeletonCard key={i} />)}</>}

            {!loading && data?.length === 0 && (
              <div
                className="rounded-2xl p-12 text-center"
                style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(51, 65, 85, 0.3)" }}
              >
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                  style={{ background: "rgba(51, 65, 85, 0.2)", border: "1px solid rgba(51, 65, 85, 0.2)" }}
                >
                  <FaDatabase className="text-2xl text-slate-600" />
                </div>
                <p className="text-slate-400 font-medium">Tidak ada profile ditemukan</p>
                <p className="text-xs text-slate-500 mt-1">
                  {keyword ? "Coba ubah pencarian" : "Belum ada data PPPoE profile"}
                </p>
              </div>
            )}

            {!loading &&
              data?.map((item) => (
                <article
                  key={item.id}
                  className={`rounded-2xl overflow-hidden transition-all duration-200 ${isSelected(item.id) ? "ring-1 ring-cyan-500/30" : ""}`}
                  style={{
                    background: isSelected(item.id) ? "rgba(0, 180, 216, 0.05)" : "rgba(15, 23, 42, 0.5)",
                    border: `1px solid ${isSelected(item.id) ? "rgba(0, 180, 216, 0.3)" : "rgba(51, 65, 85, 0.3)"}`,
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected(item.id)) {
                      e.currentTarget.style.borderColor = "rgba(0, 180, 216, 0.15)";
                      e.currentTarget.style.background = "rgba(15, 23, 42, 0.7)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected(item.id)) {
                      e.currentTarget.style.borderColor = "rgba(51, 65, 85, 0.3)";
                      e.currentTarget.style.background = "rgba(15, 23, 42, 0.5)";
                    }
                  }}
                >
                  <div className="p-4 flex justify-between items-center" style={{ borderBottom: "1px solid rgba(51, 65, 85, 0.15)" }}>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => toggleSelect(item.id)}
                        className="text-slate-400 hover:text-cyan-400 transition-colors flex-shrink-0"
                      >
                        {isSelected(item.id) ? <FaCheckSquare className="text-cyan-400 text-lg" /> : <FaSquare className="text-lg" />}
                      </button>
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{
                          background: "linear-gradient(135deg, rgba(0,180,216,0.15), rgba(0,119,182,0.08))",
                          border: "1px solid rgba(0,180,216,0.15)",
                        }}
                      >
                        <FaDatabase className="text-sm text-ocean-500" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-semibold text-gray-100 leading-tight text-sm truncate">{item.name}</h3>
                        <p className="text-[10px] text-ocean-400 font-semibold uppercase tracking-wider flex items-center gap-1 mt-0.5">
                          <FaTag className="text-[8px]" />
                          {item.rate_limit || "Unlimited"}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteClick(item)}
                      className="p-2 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-all duration-200"
                      title="Hapus"
                    >
                      <FaTrash className="text-sm" />
                    </button>
                  </div>

                  <div className="p-4 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 rounded-xl" style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(51, 65, 85, 0.25)" }}>
                        <div className="flex items-center gap-1.5 text-slate-500 mb-1.5">
                          <FaDollarSign className="text-xs" />
                          <span className="text-[10px] font-bold uppercase tracking-wider">Harga Jual</span>
                        </div>
                        <span className="text-sm font-bold text-gray-100 font-mono">{formatRupiah(item.price)}</span>
                      </div>
                      <div className="p-3 rounded-xl" style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(51, 65, 85, 0.25)" }}>
                        <div className="flex items-center gap-1.5 text-slate-500 mb-1.5">
                          <FaArrowTrendUp className="text-xs" />
                          <span className="text-[10px] font-bold uppercase tracking-wider">Fee Reseller</span>
                        </div>
                        <span className="text-sm font-bold text-emerald-400 font-mono">{formatRupiah(item.reseller_fee)}</span>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
          </section>

          {/* Mobile Bulk Delete Bar */}
          {selectedIds.length > 0 && (
            <div
              className="lg:hidden fixed bottom-0 left-0 right-0 p-4 z-40"
              style={{ background: "rgba(15, 23, 42, 0.95)", borderTop: "1px solid rgba(51, 65, 85, 0.3)", backdropFilter: "blur(20px)" }}
            >
              <button
                onClick={handleBulkDeleteClick}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold text-white bg-red-500/80 hover:bg-red-500 border border-red-500/30 transition-all"
              >
                <FaTrash className="text-xs" />
                Hapus {selectedIds.length} Profile
              </button>
            </div>
          )}
        </main>
      </DashboardLayout>

      {/* ===== Modal Create ===== */}
      <PppoeProfileCreate openModal={openModalCreate} setOpenModal={setOpenModalCreate} refresh={refreshData} />

      {/* ===== Modal Delete Single ===== */}
      <ConfirmModal
        isOpen={deleteModal}
        onClose={() => {
          setDeleteModal(false);
          setSelectedDelete(null);
        }}
        onSubmit={handleDeleteSubmit}
        loading={deleting}
        title="Hapus Profile"
        danger
        submitText="Ya, Hapus"
      >
        <div className="text-center space-y-3">
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto"
            style={{ background: "rgba(239, 68, 68, 0.1)", border: "1px solid rgba(239, 68, 68, 0.2)" }}
          >
            <FaTrash className="text-xl text-red-400" />
          </div>
          <div>
            <p className="text-gray-200 font-medium">Apakah Anda yakin ingin menghapus profile</p>
            <p className="text-cyan-400 font-bold mt-1">"{selectedDelete?.name}"?</p>
            <p className="text-slate-400 text-sm mt-2">Tindakan ini tidak dapat dibatalkan.</p>
          </div>
        </div>
      </ConfirmModal>

      {/* ===== Modal Bulk Delete ===== */}
      <ConfirmModal
        isOpen={bulkDeleteModal}
        onClose={() => setBulkDeleteModal(false)}
        onSubmit={handleBulkDeleteSubmit}
        loading={bulkDeleting}
        title="Hapus Banyak Profile"
        danger
        submitText={`Ya, Hapus ${selectedIds.length} Profile`}
      >
        <div className="text-center space-y-3">
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto"
            style={{ background: "rgba(239, 68, 68, 0.1)", border: "1px solid rgba(239, 68, 68, 0.2)" }}
          >
            <FaTrash className="text-xl text-red-400" />
          </div>
          <div>
            <p className="text-gray-200 font-medium">Apakah Anda yakin ingin menghapus</p>
            <p className="text-red-400 font-bold text-lg mt-1">{selectedIds.length} Profile?</p>
            <p className="text-slate-400 text-sm mt-2">Profile yang sedang digunakan oleh user tidak akan dihapus.</p>
          </div>
        </div>
      </ConfirmModal>
    </>
  );
};